import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../contexts/TestAuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Link } from 'react-router-dom';
import { Palette, Key, Building2, User, Shield, Users, Activity, Briefcase, AlertTriangle, HeartPulse, FileText, Calculator, FileSignature, LayoutDashboard, LifeBuoy } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import WaitlistAdmin from '../components/admin/WaitlistAdmin';
import TicketsAdmin from '../components/admin/TicketsAdmin';
import FeatureFlagsPanel from '../components/admin/FeatureFlagsPanel';
import AdminAllUsersPanel from '../components/admin/AdminAllUsersPanel';
import AdminHome from '../components/admin/AdminHome';
import BrandingSettings from '../components/BrandingSettings';
import APISettings from '../components/APISettings';
import TeamManagement from '../components/TeamManagement';
import LawFirmManagement from '../components/LawFirmManagement';
import ErrorLogViewer from '../components/ErrorLogViewer';
import DataHealthWidget from '../components/dashboard/DataHealthWidget';
import EventTemplatesManager from '../components/EventTemplatesManager';
import FutureClosureConfigPanel from '../components/FutureClosureConfigPanel';
import MyLetterhead from '../components/settings/MyLetterhead';

type TabType = 'overview' | 'letterhead' | 'organization' | 'lawfirms' | 'team' | 'branding' | 'api' | 'errors' | 'datahealth' | 'templates' | 'closure_config' | 'leads' | 'feature_flags' | 'all_users' | 'tickets';

export default function SettingsPage() {
  const { user } = useUser();
  const { role, loading: roleLoading } = usePermissions();
  const isAdmin = role === 'owner' || role === 'admin';
  const [activeTab, setActiveTab] = useState<TabType>('letterhead');

  // URL-addressable tabs (?tab=) so document-generation surfaces can deep-link the
  // empty-bar block straight to /settings?tab=letterhead.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    const allTabs: TabType[] = [
      'overview', 'letterhead', 'organization', 'lawfirms', 'team', 'templates',
      'branding', 'api', 'closure_config', 'datahealth', 'errors',
      'leads', 'feature_flags', 'all_users', 'tickets',
    ];
    if (tabParam && allTabs.includes(tabParam as TabType)) {
      setActiveTab(tabParam as TabType);
    }
  }, []);

  // Admin dashboard §4: platform admin/owner land on the live admin home
  // (Overview) when they open Settings without a specific ?tab=.
  useEffect(() => {
    const hasTabParam = !!new URLSearchParams(window.location.search).get('tab');
    if (!hasTabParam && (role === 'owner' || role === 'admin') && activeTab === 'letterhead') {
      setActiveTab('overview');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const handleTabChange = (tabId: TabType) => {
    setActiveTab(tabId);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabId);
    window.history.replaceState({}, '', url.toString());
  };

  const orgId = user?.id || 'default-org';

  // I5 §4 — role-based two-view. FIRM-VIEW: the firm's own config, visible to every
  // Settings user. ADMIN-VIEW: cross-firm + platform administration, owner/admin only.
  // The backend already enforces the admin-view operations (law_firms require_role('admin');
  // team has_permission(TEAM_*); branding SETTINGS_BRANDING; overview/activity admin-gated),
  // so this split is defense-in-depth + a cleaner surface — not the only gate. A non-admin
  // can't reach an admin-view tab even by ?tab= deep-link (guarded in the effect below).
  const personalTabs = [
    { id: 'letterhead' as const, label: 'My Letterhead', icon: FileSignature },
  ];

  // Firm-view (everyone)
  const firmTabs = [
    { id: 'organization' as const, label: 'Organization', icon: Building2 },
    { id: 'branding' as const, label: 'Branding', icon: Palette },
    { id: 'templates' as const, label: 'Event Templates', icon: FileText },
    { id: 'closure_config' as const, label: 'Closure Config', icon: Calculator },
  ];

  // Admin-view (owner/admin only). Overview = the live admin home (§4). The §9 tools
  // (Leads/All Users/Data Health/Error Logs/Feature Flags) now live in the rail System
  // group; their ?tab= deep-links still render for back-compat but aren't listed here.
  const adminViewTabs = [
    { id: 'overview' as const, label: 'Overview', icon: LayoutDashboard },
    { id: 'lawfirms' as const, label: 'Law Firms', icon: Briefcase },
    { id: 'team' as const, label: 'Team Members', icon: Users },
    { id: 'tickets' as const, label: 'Support Tickets', icon: LifeBuoy },
    { id: 'api' as const, label: 'API & Integrations', icon: Key },
  ];

  // The only tabs a non-admin may land on (firm-view). Everything else is admin-view.
  const FIRM_VIEW_TAB_IDS: TabType[] = ['letterhead', 'organization', 'branding', 'templates', 'closure_config'];

  // Access control (I5 §4): once the role resolves, a non-admin must never sit on an
  // admin-view tab — even via a ?tab= deep-link — so fall back to the firm-view default.
  // Guard on !roleLoading because role defaults to 'viewer' while permissions load, so an
  // admin deep-linking ?tab=lawfirms would otherwise be bounced before their role arrives.
  useEffect(() => {
    if (!roleLoading && !isAdmin && !FIRM_VIEW_TAB_IDS.includes(activeTab)) {
      setActiveTab('letterhead');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleLoading, isAdmin, activeTab]);

  // Keep the active tab's underline visible when the horizontal bar overflows
  // (the admin default 'overview' sits in the right-most cluster).
  const tabBarRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = tabBarRef.current?.querySelector('[aria-selected="true"]') as HTMLElement | null;
    el?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [activeTab]);

  // Horizontal underline tab (K3 gate): text-only, orange only on the active
  // 2px underline; no tint-bg / icon / pill. Replaces the 2nd vertical sidebar.
  const renderTab = (tab: { id: TabType; label: string }) => {
    const isActive = activeTab === tab.id;
    return (
      <button
        key={tab.id}
        onClick={() => handleTabChange(tab.id)}
        role="tab"
        aria-selected={isActive}
        className="px-3 py-2.5 cs-body flex-shrink-0 transition-colors hover:text-[var(--color-text-secondary)]"
        style={{
          color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
          borderBottom: isActive ? '2px solid var(--color-brand)' : '2px solid transparent',
          fontWeight: isActive ? 500 : 400,
        }}
      >
        {tab.label}
      </button>
    );
  };

  const groupDivider = (
    <span className="h-4 w-px mx-2 flex-shrink-0" style={{ background: 'var(--color-border)' }} aria-hidden />
  );

  return (
    <AppLayout>
      <div className="p-6">
        {/* Page Header — suppressed on admin tabs whose panel renders its own
            masthead (All Users, Support Tickets), so the page doesn't show a
            stacked double-header. The tab bar + the panel header carry wayfinding. */}
        {!['all_users', 'tickets'].includes(activeTab) && (
          <div className="mb-6">
            <h1 className="cs-display">Settings</h1>
            <p className="cs-meta">Manage your organization and preferences</p>
          </div>
        )}

        <div>
          {/* Horizontal tab bar — the app rail is the only left column; content runs full
              width. K3 gate: underline bar, text-only, hairline-separated clusters, orange
              only on the active 2px underline. I5 §4 role split: the FIRM-VIEW cluster shows
              for everyone; the ADMIN-VIEW cluster renders only for owner/admin, so its
              presence is the permission signal (no pills). */}
          <nav
            ref={tabBarRef}
            className="flex items-center gap-1 mb-6 overflow-x-auto whitespace-nowrap"
            style={{ borderBottom: '1px solid var(--color-border)' }}
            aria-label="Settings sections"
            role="tablist"
          >
            {personalTabs.map(renderTab)}

            {groupDivider}
            {firmTabs.map(renderTab)}

            {isAdmin && (
              <>
                {groupDivider}
                {adminViewTabs.map(renderTab)}
                <Link
                  to="/settings/activity"
                  className="px-3 py-2.5 cs-body flex-shrink-0 transition-colors hover:text-[var(--color-text-secondary)]"
                  style={{ color: 'var(--color-text-muted)', borderBottom: '2px solid transparent' }}
                >
                  Activity Log
                </Link>
              </>
            )}
          </nav>

          {/* Main Content */}
          <div className="min-w-0">
            {/* Letterhead Tab */}
            {activeTab === 'letterhead' && <MyLetterhead />}

            {/* Organization Tab */}
            {activeTab === 'organization' && (
              <div
                className="rounded-xl overflow-hidden"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              >
                <div className="px-6 py-4" style={{ background: 'var(--color-brand)' }}>
                  <div className="flex items-center gap-3">
                    <Building2 className="w-6 h-6" style={{ color: 'var(--color-on-brand)' }} />
                    <div>
                      <h2 className="cs-h2" style={{ color: 'var(--color-on-brand)' }}>Organization Details</h2>
                      <p className="cs-meta" style={{ color: 'var(--color-on-brand)' }}>Manage your organization information</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Organization Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block cs-label mb-1">Organization ID</label>
                      <div
                        className="px-4 py-2.5 rounded-lg"
                        style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                      >
                        <code className="cs-figure">{orgId.substring(0, 20)}...</code>
                      </div>
                      <p className="cs-meta mt-1">Used for API integrations</p>
                    </div>
                    <div>
                      <label className="block cs-label mb-1">Account Type</label>
                      <div
                        className="px-4 py-2.5 rounded-lg"
                        style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                      >
                        <span className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>Professional</span>
                      </div>
                    </div>
                  </div>

                  {/* Team Members - Now using TeamManagement */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="cs-h3">Team Members</h3>
                      <button
                        onClick={() => setActiveTab('team')}
                        className="px-3 py-1 rounded-lg cs-body transition"
                        style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                      >
                        Manage Team →
                      </button>
                    </div>
                    <div
                      className="rounded-lg p-4"
                      style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                    >
                      <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
                        Team management is now available. Invite members, assign roles, and control access.
                      </p>
                    </div>
                  </div>

                  {/* Security */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                      <h3 className="cs-h3">Security</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        className="p-4 rounded-lg"
                        style={{ background: 'transparent', border: '1px solid var(--color-border)' }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-2 h-2 rounded-full" style={{ background: 'var(--color-verified)' }}></div>
                          <span className="cs-body" style={{ color: 'var(--color-verified)', fontWeight: 500 }}>Clerk Authentication</span>
                        </div>
                        <p className="cs-meta" style={{ color: 'var(--color-verified)' }}>Secure authentication enabled</p>
                      </div>
                      <div
                        className="p-4 rounded-lg"
                        style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-2 h-2 rounded-full" style={{ background: 'var(--color-brand)' }}></div>
                          <span className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>API Security</span>
                        </div>
                        <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>Rate limiting & key authentication</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Law Firms Tab */}
            {activeTab === 'lawfirms' && (
              <div
                className="rounded-xl overflow-hidden"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              >
                <LawFirmManagement />
              </div>
            )}

            {/* Branding Tab — BrandingSettings self-persists (GET/PUT/POST
                /api/v1/branding/{orgId}); no onSave callback needed here. */}
            {activeTab === 'branding' && <BrandingSettings orgId={orgId} />}

            {/* Team Tab */}
            {activeTab === 'team' && (
              <div
                className="rounded-xl overflow-hidden"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              >
                <div className="p-6">
                  <TeamManagement />
                </div>
              </div>
            )}

            {/* Event Templates Tab */}
            {activeTab === 'templates' && (
              <div
                className="rounded-xl overflow-hidden"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              >
                <div className="px-6 py-4" style={{ background: 'var(--color-brand)' }}>
                  <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6" style={{ color: 'var(--color-on-brand)' }} />
                    <div>
                      <h2 className="cs-h2" style={{ color: 'var(--color-on-brand)' }}>Event Templates</h2>
                      <p className="cs-meta" style={{ color: 'var(--color-on-brand)' }}>Pre-configured settings for different disaster types</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <EventTemplatesManager />
                </div>
              </div>
            )}

            {/* API Tab */}
            {activeTab === 'api' && (
              <APISettings orgId={orgId} />
            )}

            {/* Future Closure Config Tab */}
            {activeTab === 'closure_config' && (
              <div
                className="rounded-xl overflow-hidden"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              >
                <div className="px-6 py-4" style={{ background: 'var(--color-brand)' }}>
                  <div className="flex items-center gap-3">
                    <Calculator className="w-6 h-6" style={{ color: 'var(--color-on-brand)' }} />
                    <div>
                      <h2 className="cs-h2" style={{ color: 'var(--color-on-brand)' }}>Future Closure Configuration</h2>
                      <p className="cs-meta" style={{ color: 'var(--color-on-brand)' }}>Manage default multipliers, continuation rates, and calculation parameters</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <FutureClosureConfigPanel />
                </div>
              </div>
            )}

            {/* Error Logs Tab - Owners/Admins Only */}
            {activeTab === 'errors' && (role === 'owner' || role === 'admin') && (
              <div
                className="rounded-xl overflow-hidden"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              >
                <div className="px-6 py-4" style={{ background: 'var(--color-brand)' }}>
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6" style={{ color: 'var(--color-on-brand)' }} />
                    <div>
                      <h2 className="cs-h2" style={{ color: 'var(--color-on-brand)' }}>API Error Logs</h2>
                      <p className="cs-meta" style={{ color: 'var(--color-on-brand)' }}>Monitor and resolve frontend API errors</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <ErrorLogViewer />
                </div>
              </div>
            )}

            {/* Data Health Tab - Owners/Admins Only */}
            {activeTab === 'datahealth' && (role === 'owner' || role === 'admin') && (
              <div className="space-y-6">
                <div className="rounded-xl px-6 py-4" style={{ background: 'var(--color-brand)' }}>
                  <div className="flex items-center gap-3">
                    <HeartPulse className="w-6 h-6" style={{ color: 'var(--color-on-brand)' }} />
                    <div>
                      <h2 className="cs-h2" style={{ color: 'var(--color-on-brand)' }}>Data Health Dashboard</h2>
                      <p className="cs-meta" style={{ color: 'var(--color-on-brand)' }}>Monitor data quality metrics across all claims</p>
                    </div>
                  </div>
                </div>
                <DataHealthWidget />
              </div>
            )}

            {/* Leads Tab - Owners/Admins Only */}
            {activeTab === 'leads' && (role === 'owner' || role === 'admin') && <WaitlistAdmin />}

            {/* Support Tickets Tab - Owners/Admins Only (I5 §B) */}
            {activeTab === 'tickets' && isAdmin && <TicketsAdmin />}

            {/* Feature Flags Tab - Owners/Admins Only */}
            {activeTab === 'feature_flags' && (role === 'owner' || role === 'admin') && <FeatureFlagsPanel />}
            {activeTab === 'overview' && (role === 'owner' || role === 'admin') && (
              <div className="space-y-6">
                <div>
                  <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Overview</h2>
                  <p className="cs-meta mt-1" style={{ color: 'var(--color-text-muted)' }}>
                    Live across the platform — click a tile to jump in.
                  </p>
                </div>
                <AdminHome />
              </div>
            )}
            {activeTab === 'all_users' && (role === 'owner' || role === 'admin') && <AdminAllUsersPanel />}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
